// src/components/TripsPage.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import PriceTag from './PriceTag';

// Styled Components
const TripsContainer = styled.div`
    min-height: 100vh;
    background: #ffffff;
    color: #1a1a1a;
    padding: 80px 0 20px;
    display: flex;
    align-items: center;
`;

const TripsContent = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    width: 100%;
`;

const PageHeader = styled(motion.div)`
    text-align: center;
    margin-bottom: 3rem;

    h1 {
        font-size: 3.5rem;
        font-weight: 800;
        margin-bottom: 1rem;
        background: linear-gradient(135deg, #667eea, #764ba2);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1.1;

        @media (max-width: 768px) {
            font-size: 2.5rem;
        }
    }

    p {
        font-size: 1.2rem;
        color: #666;
        line-height: 1.5;
        max-width: 500px;
        margin: 0 auto;
        font-weight: 400;
    }
`;

const TripsGrid = styled.div`
    display: grid;
    /* Cap card width so a single tour doesn't stretch across the whole page */
    grid-template-columns: repeat(auto-fit, minmax(380px, 600px));
    justify-content: center;
    gap: 2rem;
    margin-bottom: 3rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1.5rem;
    }
`;

const TripCard = styled(motion.div)`
    background: #ffffff;
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    position: relative;
    cursor: pointer;
    height: fit-content;

    &:hover {
        transform: translateY(-5px);
        border-color: #667eea;
        box-shadow: 0 15px 30px rgba(102, 126, 234, 0.15);
    }
`;

const TripImage = styled.div<{ bgImage: string }>`
    height: 220px;
    background:
            linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1)),
            url(${props => props.bgImage}) center/cover;
    position: relative;
    overflow: hidden;
`;

const TripBadge = styled.div<{ type: string }>`
    position: absolute;
    top: 1rem;
    left: 1rem;
    background: ${props => {
        switch (props.type) {
            case 'Most Popular': return 'linear-gradient(135deg, #667eea, #764ba2)';
            case 'Cultural': return 'linear-gradient(135deg, #10b981, #059669)';
            case 'Adventure': return 'linear-gradient(135deg, #f59e0b, #d97706)';
            default: return 'linear-gradient(135deg, #667eea, #764ba2)';
        }
    }};
    color: white;
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const TripContent = styled.div`
    padding: 2rem;
`;

const TripHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;

    @media (max-width: 480px) {
        flex-direction: column;
        gap: 0.5rem;
    }
`;

const TripTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
    color: #1a1a1a;
    line-height: 1.3;
    flex: 1;
`;

const TripDescription = styled.p`
    color: #666;
    line-height: 1.5;
    margin-bottom: 1.5rem;
    font-size: 1rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const TripFeatures = styled.ul`
    list-style: none;
    padding: 0;
    margin-bottom: 2rem;

    li {
        color: #666;
        margin-bottom: 0.5rem;
        padding-left: 1.25rem;
        position: relative;
        font-size: 0.9rem;

        &::before {
            content: '✓';
            position: absolute;
            left: 0;
            color: #667eea;
            font-weight: bold;
        }
    }
`;

const TripButton = styled(motion.button)`
    width: 100%;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    padding: 1rem 1.5rem;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    }
`;

const AdditionalInfo = styled(motion.div)`
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    border-radius: 16px;
    padding: 2rem;
    text-align: center;
    border: 1px solid #e5e7eb;
    margin-top: 2rem;

    h2 {
        font-size: 1.75rem;
        font-weight: 700;
        margin-bottom: 0.75rem;
        color: #1a1a1a;
    }

    p {
        color: #666;
        line-height: 1.5;
        margin-bottom: 1.5rem;
        font-size: 1rem;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;
    }
`;

const ContactButton = styled(motion.button)`
    background: transparent;
    color: #667eea;
    border: 2px solid #667eea;
    padding: 0.75rem 2rem;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: #667eea;
        color: white;
        transform: translateY(-2px);
    }
`;

// Styled Components (keep all your existing styles, just updating the component logic)

// Main TripsPage Component
interface TripsPageProps {
    onNavigateToContact: () => void;
    onNavigateToTripDetail: () => void;
}

const TripsPage: React.FC<TripsPageProps> = ({ onNavigateToContact, onNavigateToTripDetail }) => {
    const featuredTrips = [
        {
            id: 1,
            title: "From Bratislava: Castles, Čičmany & Bratislava Castle",
            subtitle: "Historic Castles & Traditional Village",
            description: "Discover Slovakia's rich history on a tour from Bratislava. Visit Červený Kameň, Smolenice and Trenčín Castles, explore the village of Čičmany, Slovak Bethlehem and end at the Bratislava Castle.",
            originalPrice: "169€",
            price: "119€",
            duration: "10 hours",
            groupSize: "2-7 people",
            includes: ["English/Slovak guide", "Comfortable vehicle", "Bratislava hotel pickup/dropoff", "Castle entrances included"],
            image: "https://upload.wikimedia.org/wikipedia/commons/b/b4/Bratislava_-_Burg_%28b%29.JPG",
            badge: "Cultural",
            type: "slovakia" as const
        }
    ];

    const handleViewDetails = () => {
        onNavigateToTripDetail();
    };

    return (
        <TripsContainer>
            <TripsContent>
                <PageHeader
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <h1>Featured Tours</h1>
                    <p>
                        Discover the best of Central Europe with our carefully curated experiences.
                        From imperial cities to alpine adventures, each tour is designed to create unforgettable memories.
                    </p>
                </PageHeader>

                <TripsGrid>
                    {featuredTrips.map((trip, index) => (
                        <TripCard
                            key={trip.id}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.2 }}
                            whileHover={{ y: -5 }}
                            onClick={handleViewDetails}
                        >
                            <TripImage bgImage={trip.image}>
                                <TripBadge type={trip.badge}>{trip.badge}</TripBadge>
                            </TripImage>

                            <TripContent>
                                <TripHeader>
                                    <div>
                                        <TripTitle>{trip.title}</TripTitle>
                                        <p style={{ color: '#667eea', fontWeight: 500, margin: 0, fontSize: '0.9rem' }}>{trip.subtitle}</p>
                                    </div>
                                    <PriceTag
                                        prefix="From "
                                        originalPrice={trip.originalPrice}
                                        price={trip.price}
                                        size="sm"
                                        align="end"
                                    />
                                </TripHeader>

                                <TripDescription>{trip.description}</TripDescription>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '1rem',
                                    marginBottom: '1.5rem',
                                    background: '#f8fafc',
                                    padding: '1rem',
                                    borderRadius: '10px'
                                }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: '#667eea', fontWeight: 600, fontSize: '0.8rem', marginBottom: '0.25rem' }}>DURATION</div>
                                        <div style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.9rem' }}>{trip.duration}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: '#667eea', fontWeight: 600, fontSize: '0.8rem', marginBottom: '0.25rem' }}>GROUP SIZE</div>
                                        <div style={{ color: '#1a1a1a', fontWeight: 600, fontSize: '0.9rem' }}>{trip.groupSize}</div>
                                    </div>
                                </div>

                                <TripFeatures>
                                    {trip.includes.map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                    ))}
                                </TripFeatures>

                                <TripButton
                                    onClick={handleViewDetails}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    View Details & Book
                                </TripButton>
                            </TripContent>
                        </TripCard>
                    ))}
                </TripsGrid>

                <AdditionalInfo
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                >
                    <h2>Looking for Something Else?</h2>
                    <p>
                        We offer many more tours and custom experiences throughout Central Europe.
                        Contact us to create the perfect itinerary for you.
                    </p>
                    <ContactButton
                        onClick={onNavigateToContact}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Contact Us for Custom Tours
                    </ContactButton>
                </AdditionalInfo>
            </TripsContent>
        </TripsContainer>
    );
};

export default TripsPage;
